import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import L from 'leaflet';
import { Circle, MapContainer, Marker, TileLayer, Tooltip, ZoomControl } from 'react-leaflet';
import { useMap } from 'react-leaflet/hooks';
import { ChevronLeft, ChevronRight, DirectionsCar, DirectionsWalk } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import { ConfigContext } from '../../../contexts/config-context';
import LocationAPI from '../../../axios/locations';

const ZOOM = 15;
const AREA_RADIUS_M = 150;
const MAX_NEARBY = 5;
export const TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';

// Esri has no tiles of this style past this zoom; closer in, the last level is stretched
export const TILES_MAX_ZOOM = 16;

export const homeIcon = L.divIcon({
    className: 'hs-loc-pin-anchor',
    iconSize: [0, 0],
    html: '<svg class="hs-loc-pin" width="44" height="52" viewBox="0 0 44 52" fill="none"><path d="M22 51s18-15 18-29A18 18 0 0 0 4 22c0 14 18 29 18 29Z" fill="#0073F0" stroke="#fff" stroke-width="3"></path><path d="M14 22.5 22 16l8 6.5V30a1 1 0 0 1-1 1h-4.5v-5h-5v5H15a1 1 0 0 1-1-1Z" fill="#fff"></path></svg>',
});

// the same number as the place's row in the list under the map; made once per
// number, since a new icon replaces the marker's element and replays its fade-in
const numberIcons = {};
const numberIcon = (number) => numberIcons[number] || (numberIcons[number] = L.divIcon({
    className: 'hs-loc-pin-anchor',
    iconSize: [0, 0],
    html: `<span class="hs-loc-num">${number}</span>`,
}));

const formatDistance = (m) => m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`;

// the distance is in a straight line, while streets wind, hence the detour factors;
// about 80 m a minute on foot and 400 m a minute (24 km/h) by car in town
const MAX_WALK_MIN = 20;
const travelTime = (m) => {
    const walk = Math.max(1, Math.ceil(m * 1.3 / 80));
    if (walk <= MAX_WALK_MIN) return { walk: true, minutes: walk };
    return { walk: false, minutes: Math.max(1, Math.ceil(m * 1.4 / 400)) };
};

// coordinates can arrive as text
const readPlaces = (nearby) => Object.fromEntries(Object.entries(nearby).map(([name, places]) => [name, places
    .map(place => ({ ...place, coordinates: place.coordinates.map(Number) }))
    .filter(place => place.coordinates.every(Number.isFinite))
    .slice(0, MAX_NEARBY)]));

const placeKey = (place) => `${place.name}-${place.coordinates.join(',')}`;

// zooms out just enough to show the listing together with the places found
const FitNearby = ({ position, places }) => {
    const map = useMap();

    useEffect(() => {
        if (places.length === 0) return;
        const bounds = L.latLngBounds([position, ...places.map(place => place.coordinates)]);
        const options = { padding: [40, 40], maxZoom: ZOOM };
        // when the places fit at the same zoom, the map only glides over, with no
        // stretched tiles while the sharp ones load
        if (Math.min(map.getBoundsZoom(bounds, false, L.point(80, 80)), ZOOM) === map.getZoom()) {
            map.panTo(bounds.getCenter(), { animate: true, duration: 0.6, easeLinearity: 0.4 });
            return;
        }
        map.flyToBounds(bounds, { ...options, duration: 0.8, easeLinearity: 0.4 });
    }, [map, position, places]);

    return null;
};

// below this, a press on a category is a click; past it, a drag of the row
const DRAG_THRESHOLD_PX = 5;

// a row of categories that scrolls sideways, with arrows where more is hidden;
// a finger scrolls it natively, a mouse by dragging it
const CategoryScroller = ({ children }) => {
    const ref = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: true });
    const drag = useRef(null);
    // the click that ends a drag must not also pick the category under the mouse
    const dragged = useRef(false);

    const startDrag = (e) => {
        dragged.current = false;
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag.current = { pointerId: e.pointerId, x: e.clientX, left: ref.current.scrollLeft };
    };

    const moveDrag = (e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        if (!dragged.current && Math.abs(dx) > DRAG_THRESHOLD_PX) {
            dragged.current = true;
            // captured only now, so a plain click still lands on its button
            ref.current.setPointerCapture(d.pointerId);
            ref.current.classList.add('dragging');
        }
        if (dragged.current) ref.current.scrollLeft = d.left - dx;
    };

    const endDrag = () => {
        drag.current = null;
        ref.current?.classList.remove('dragging');
    };

    const swallowClick = (e) => {
        if (!dragged.current) return;
        dragged.current = false;
        e.preventDefault();
        e.stopPropagation();
    };

    const update = () => {
        const el = ref.current;
        if (!el) return;
        setEdges({ start: el.scrollLeft <= 1, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 });
    };

    useEffect(() => {
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [children]);

    const scroll = (direction) => ref.current?.scrollBy({ left: direction * ref.current.clientWidth * 0.7, behavior: 'smooth' });

    return (
        <div className={`hs-loc-scroller${edges.start ? '' : ' more-start'}${edges.end ? '' : ' more-end'}`}>
            {!edges.start && <button className='hs-loc-arrow start' onClick={() => scroll(-1)} aria-label='‹'><ChevronLeft /></button>}
            <div className='hs-loc-cats' ref={ref} onScroll={update} onPointerDown={startDrag} onPointerMove={moveDrag}
                onPointerUp={endDrag} onPointerCancel={endDrag} onClickCapture={swallowClick}>
                {children}
            </div>
            {!edges.end && <button className='hs-loc-arrow end' onClick={() => scroll(1)} aria-label='›'><ChevronRight /></button>}
        </div>
    );
};

export const LocationSection = ({ listing }) => {
    const { locationLegend } = useContext(ConfigContext);
    const { t } = useTranslation(["details", "location"]);
    const [category, setCategory] = useState(null);
    // the place under the pointer, lit both on the map and in the list
    const [hovered, setHovered] = useState(null);
    // 'loading', 'error', or the places of each kind: { kindergartens: [...], ... }
    const [nearby, setNearby] = useState(() => listing.nearby ? readPlaces(listing.nearby) : 'loading');

    const position = useMemo(() => [parseFloat(listing.lat), parseFloat(listing.lng)], [listing.lat, listing.lng]);
    const hasPosition = Number.isFinite(position[0]) && Number.isFinite(position[1]);
    const label = [listing.location?.label, listing.subplace?.label].filter(Boolean).join(', ') || listing.place?.label;

    useEffect(() => {
        setCategory(null);
        if (listing.nearby) {
            setNearby(readPlaces(listing.nearby));
            return;
        }
        // not found ahead yet, e.g. a listing published before they were
        let cancelled = false;
        setNearby('loading');
        LocationAPI.getNearby(listing.id)
            .then(res => !cancelled && setNearby(readPlaces(res.data)))
            .catch(() => !cancelled && setNearby('error'));
        return () => { cancelled = true; };
    }, [listing.id, listing.nearby]);

    const loaded = typeof nearby === 'object';
    // a kind with nothing in reach is left out rather than shown empty
    const categories = loaded ? locationLegend.filter(item => nearby[item.name]?.length > 0) : [];
    // the first kind is shown at once, so the list is never empty to begin with
    const current = categories.find(item => item.name === category) || categories[0];
    const places = useMemo(() => current ? nearby[current.name] : [], [current, nearby]);

    // the hovered number is lit through its class, keeping the icon as it is
    const markers = useRef({});
    useEffect(() => {
        Object.entries(markers.current).forEach(([key, marker]) =>
            marker?.getElement()?.querySelector('.hs-loc-num')?.classList.toggle('on', key === hovered));
    }, [hovered, places]);

    if (!hasPosition) return null;

    const closest = places.length > 0 ? travelTime(places[0].meters) : null;

    return (
        <section id='lokacija' className='hs-det-section hs-det-location'>
            <h2>{t('location')}</h2>
            <div className='hs-loc-map'>
                <MapContainer center={position} zoom={ZOOM} zoomControl={false} scrollWheelZoom={false}>
                    {/* tiles around the view are kept, so moving between categories shows them at once */}
                    <TileLayer url={TILES} attribution='&copy; Esri' maxNativeZoom={TILES_MAX_ZOOM} keepBuffer={4} />
                    <ZoomControl position='topright' />
                    <Circle center={position} radius={AREA_RADIUS_M} pathOptions={{ color: '#0073F0', weight: 1.5, opacity: 0.4, fillColor: '#0073F0', fillOpacity: 0.14 }} />
                    <Marker position={position} icon={homeIcon} interactive={false} />
                    <FitNearby position={position} places={places} />
                    {places.map((place, index) => (
                        <Marker key={placeKey(place)} position={place.coordinates} icon={numberIcon(index + 1)}
                            ref={marker => { markers.current[placeKey(place)] = marker; }}
                            zIndexOffset={hovered === placeKey(place) ? 1000 : 0}
                            eventHandlers={{ mouseover: () => setHovered(placeKey(place)), mouseout: () => setHovered(null) }}>
                            <Tooltip direction='top' offset={[0, -14]}>{place.name} · {formatDistance(place.meters)}</Tooltip>
                        </Marker>
                    ))}
                </MapContainer>
                {label && <div className='hs-loc-label'>{label}</div>}
            </div>

            <div className='hs-loc-panel'>
                <div className='hs-loc-head'>
                    <div>
                        <h3>{t('nearby_title')}</h3>
                        {nearby === 'loading' && <p>{t('nearby_loading')}</p>}
                        {nearby === 'error' && <p>{t('nearby_error')}</p>}
                        {loaded && categories.length === 0 && <p>{t('nearby_empty')}</p>}
                        {closest &&
                            <p>{t('nearby_summary', {
                                category: t(current.name, { ns: 'location' }),
                                count: places.length,
                                minutes: closest.minutes,
                                how: t(closest.walk ? 'nearby_walk' : 'nearby_drive'),
                            })}</p>}
                    </div>
                    {categories.length > 0 && <span className='hs-loc-legend'><DirectionsWalk />{t('nearby_legend')}</span>}
                </div>

                {categories.length > 0 &&
                    <CategoryScroller>
                        {categories.map(item => (
                            <button key={item.name} className={item === current ? 'on' : ''} onClick={() => setCategory(item.name)}>
                                {item.icon}{t(item.name, { ns: 'location' })}
                            </button>
                        ))}
                    </CategoryScroller>}

                {places.length > 0 &&
                    <ol className='hs-loc-list'>
                        {places.map((place, index) => {
                            const { walk, minutes } = travelTime(place.meters);
                            return (
                                <li key={placeKey(place)} className={hovered === placeKey(place) ? 'on' : ''}
                                    onMouseEnter={() => setHovered(placeKey(place))} onMouseLeave={() => setHovered(null)}>
                                    <span className='hs-loc-num'>{index + 1}</span>
                                    <span className='hs-loc-name'>{place.name}</span>
                                    <span className='hs-loc-dist'>{formatDistance(place.meters)}</span>
                                    <strong aria-label={t(walk ? 'nearby_walk' : 'nearby_drive')}>
                                        {walk ? <DirectionsWalk /> : <DirectionsCar />}{minutes} min
                                    </strong>
                                </li>
                            );
                        })}
                    </ol>}
            </div>
        </section>
    );
};
